// PRD 16 C1 — the provenance & audit trail, at /trust/provenance.
//
// NOT AN ANALYTICS VIEW, and not a metric anywhere on it. Its reader is a
// program office or a regulated-industry reviewer asking one question about a
// controlled document set: what here was written by a machine, and who
// accepted it. An SMB will never open this page.
//
// SAME SURFACE AS /trust, DIFFERENT GATE — and the difference is the whole
// design note. PRD 16's decision D2 puts Tier C on a surface separate from
// the analytics dashboard, which is why this sits beside the data-collection
// ledger and reuses its chassis and its stylesheet. But C2 renders a
// build-time constant and is deliberately readable before purchase, whereas
// this page renders one org's real record, so it is org-scoped and the
// unauthenticated state is a sign-in prompt rather than a blank page. Two
// pages, one surface, one of them gated.
//
// THE RULE THIS SCREEN IS BUILT AROUND: never present a partial trail as a
// complete one. So incompleteness is not an error state here and is not
// tucked into a footnote — the gap count is the first thing rendered after
// the title, ahead of any of the record it is a hole in, and the structural
// limits (`provenanceLimits.ts`) are shown even when the API reports zero
// gaps, because those are holes the server cannot detect in itself.
//
// COUNSEL REVIEW PENDING (docs/business/05 §5.2) — no copy on this screen has
// been reviewed by counsel. There is deliberately no "verified" or
// "compliant" badge and no standard is named anywhere: what this record
// evidences is the reader's auditor's decision, not ours. Keep it that way.

import "../../tokens/paper.css";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  SkeletonStack,
  StatRow,
  StatTile,
  Table,
} from "../../ui";
import {
  allGaps,
  gapSummary,
  useOrgTrail,
  type CorpusTrail,
  type DocTrail,
  type Gap,
  type OrgTrail,
} from "./provenanceApi";
import { STRUCTURAL_LIMITS, gapLabel } from "./provenanceLimits";
import "./trust.css";
import "./provenance.css";

function when(iso: string | null): string {
  if (!iso) return "not recorded";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "not recorded" : at.toISOString().slice(0, 10);
}

/** An absent value is stated, never rendered as a blank cell or a zero. */
function orUnrecorded(value: string | number | null): string {
  return value === null || value === "" ? "not recorded" : String(value);
}

function GapList({ gaps }: { gaps: Gap[] }) {
  return (
    <ul className="maisight-prov-gaps">
      {gaps.map((gap, i) => (
        <li className="maisight-prov-gap" key={`${gap.code}-${gap.subject}-${i}`}>
          <p className="maisight-prov-gap-head">
            <Badge tone="flagged">{gapLabel(gap.code)}</Badge>
            <span className="maisight-trust-field type-numSm">{gap.subject}</span>
            {gap.affected > 0 ? (
              <span className="type-labelSm">
                {gap.affected} {gap.affected === 1 ? "record" : "records"}
              </span>
            ) : null}
          </p>
          <p className="maisight-prov-gap-detail type-bodyS">{gap.detail}</p>
        </li>
      ))}
    </ul>
  );
}

function DocumentTable({ docs }: { docs: DocTrail[] }) {
  return (
    <Table
      label="Provenance per document"
      rows={docs}
      rowKey={(d) => d.doc_id}
      // NOTHING IS WITHHELD HERE. `Table` caps at 25 by default, which is
      // right for a 480-term leaderboard and wrong for a record whose only
      // value is that it is complete — a provenance table that hid rows
      // behind "show more" is the omission this screen exists to prevent.
      maxRows={docs.length}
      total={docs.length}
      unit="documents"
      columns={[
        {
          key: "doc",
          header: "Document",
          cell: (d) => (
            <>
              <span className="maisight-prov-title">{d.title ?? d.doc_id}</span>
              <span className="maisight-trust-field type-numSm">{d.doc_id}</span>
            </>
          ),
        },
        {
          key: "published",
          header: "Published by",
          cell: (d) => (
            <>
              <span>{orUnrecorded(d.published_by)}</span>
              <span className="maisight-prov-sub type-labelSm">
                {when(d.published_at)}
              </span>
            </>
          ),
        },
        {
          key: "model",
          header: "Model",
          // Never "—". An empty model list on a machine-written glossary is a
          // gap, and the row says so rather than looking like "no model".
          cell: (d) =>
            d.models.length > 0 ? (
              d.models.join(", ")
            ) : d.machine_term_count > 0 ? (
              <Badge tone="flagged">not recorded</Badge>
            ) : (
              "no model involved"
            ),
        },
        {
          key: "decisions",
          header: "Accepted / edited / added",
          align: "right",
          cell: (d) => `${d.accepted} / ${d.edited} / ${d.added}`,
        },
        {
          key: "versions",
          header: "Parser / extraction / published",
          align: "right",
          cell: (d) =>
            [d.pipeline_version, d.extraction_glossary_version, d.published_version]
              .map((v) => (v === null ? "?" : v))
              .join(" / "),
        },
      ]}
    />
  );
}

function CorpusTable({ corpora }: { corpora: CorpusTrail[] }) {
  return (
    <Table
      label="Reconciliation per corpus"
      rows={corpora}
      rowKey={(c) => c.corpus_id}
      maxRows={corpora.length}
      total={corpora.length}
      unit="corpora"
      columns={[
        {
          key: "corpus",
          header: "Corpus",
          cell: (c) => (
            <span className="maisight-trust-field type-numSm">{c.corpus_id}</span>
          ),
        },
        {
          key: "runs",
          header: "Runs",
          align: "right",
          cell: (c) => c.reconcile_run_count,
        },
        {
          key: "last",
          header: "Last run",
          cell: (c) => when(c.last_run_at),
        },
        {
          key: "decisions",
          header: "Reviewer decisions",
          align: "right",
          cell: (c) => c.decision_count,
        },
        {
          key: "unreviewed",
          header: "Live but unreviewed",
          align: "right",
          cell: (c) =>
            c.unreviewed_canonical_count > 0 ? (
              <Badge tone="flagged">{c.unreviewed_canonical_count}</Badge>
            ) : (
              0
            ),
        },
      ]}
    />
  );
}

function Trail({ trail }: { trail: OrgTrail }) {
  // Guard on the arrays, never on `trail` — the payload is an `as` cast over
  // untyped JSON, so a truthy object proves nothing about what is inside it.
  const docs = Array.isArray(trail.docs) ? trail.docs : [];
  const corpora = Array.isArray(trail.corpora) ? trail.corpora : [];
  const gaps = allGaps(trail);
  const summary = gapSummary(gaps);
  const machineTerms = docs.reduce((n, d) => n + d.machine_term_count, 0);

  return (
    <>
      {/* FIRST, ahead of the record. A reader who stops here must still have
          been told the record is incomplete. */}
      <Card header="Completeness of this record">
        <StatRow>
          <StatTile label="Documents" value={String(docs.length)} />
          <StatTile label="Machine-written terms" value={String(machineTerms)} />
          <StatTile label="Gaps found" value={String(gaps.length)} />
        </StatRow>
        {gaps.length === 0 ? (
          <p className="maisight-trust-lede type-bodyS">
            Nothing is missing from the record we can detect. That is not the
            same as the record being complete — the limits below are properties
            of how it is written, and no scan of the stored data can find them.
          </p>
        ) : (
          <>
            <p className="maisight-trust-lede type-bodyS">
              This record has holes, listed in full below. They are shown
              because a provenance trail that omitted what it could not find
              would be worse than none at all. Nothing here is inferred or
              filled in: where the answer was not recorded, it is missing.
            </p>
            <ul className="maisight-prov-summary">
              {summary.map((row) => (
                <li className="maisight-prov-summary-row" key={row.code}>
                  <Badge tone="flagged">{gapLabel(row.code)}</Badge>
                  <span className="type-labelSm">
                    {row.affected > 0
                      ? `${row.affected} records`
                      : `${row.occurrences} occurrences`}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {gaps.length > 0 ? (
        <Card header="Every gap, in full">
          <GapList gaps={gaps} />
        </Card>
      ) : null}

      <Card header="What this record cannot tell you">
        <p className="maisight-trust-lede type-bodyS">
          These hold whatever the data looks like. They describe how the record
          is written, so no scan of it can surface them and they are stated
          here instead. Each names the file it comes from.
        </p>
        <ul className="maisight-trust-claims">
          {STRUCTURAL_LIMITS.map((entry) => (
            <li className="maisight-trust-claim" key={entry.source + entry.limit}>
              <p className="maisight-trust-claim-text type-bodyS">{entry.limit}</p>
              <p className="maisight-prov-because type-bodyS">{entry.because}</p>
              <p className="maisight-trust-source type-labelSm">{entry.source}</p>
            </li>
          ))}
        </ul>
      </Card>

      <Card header="Per document">
        {docs.length === 0 ? (
          <EmptyState
            headline="No documents on this account"
            body="A provenance record appears here once a document has been processed. This is not a gap in the trail — there is nothing yet to attest to."
            icon="file-text"
          />
        ) : (
          <DocumentTable docs={docs} />
        )}
      </Card>

      <Card header="Cross-document reconciliation">
        {corpora.length === 0 ? (
          <EmptyState
            headline="No corpus on this account"
            body="Reconciliation merges concepts defined across several documents. Nothing has run because no corpus exists."
            icon="file-text"
          />
        ) : (
          <CorpusTable corpora={corpora} />
        )}
      </Card>
    </>
  );
}

export function ProvenanceScreen() {
  const query = useOrgTrail();

  return (
    <div className="maisight-trust-page" data-chassis="paper">
      <main className="maisight-trust-body">
        <PageHeader
          eyebrow="Provenance"
          title="What a machine wrote, and who accepted it"
          description="Assembled from the stored record of this account, not from a policy document. It covers how every definition was produced and what decision was taken on it. Where the record does not answer a question, this page says so rather than leaving the question out."
        />

        {query.isPending ? (
          <SkeletonStack label="Loading the provenance record">
            <Skeleton shape="line" width={280} height={28} />
            <Skeleton shape="block" />
            <Skeleton shape="block" />
          </SkeletonStack>
        ) : null}

        {query.isError ? (
          query.error.status === 401 ? (
            <EmptyState
              headline="Sign in to see this account's record"
              body="The provenance trail covers one account's own documents and is never shown to another. Signing in is what decides which record you are looking at."
              icon="info"
            />
          ) : (
            <ErrorState
              headline="The record could not be read"
              detail="Nothing is shown rather than a partial one, because a provenance trail is only worth reading if it is whole."
            />
          )
        ) : null}

        {query.data ? <Trail trail={query.data} /> : null}
      </main>
    </div>
  );
}
